/* GRANDEVO — Marquee Letter Builder
 * Lightweight SVG preview (no WebGL). One instance per [data-marquee-builder] element.
 * Prices are never computed here for checkout: in Purchase Mode the cart line uses a real Shopify
 * variant (height × lighting × finish), so the checkout price always comes from Shopify.
 */
(function () {
  'use strict';
  const SVGNS = 'http://www.w3.org/2000/svg';
  const STANDARD = /^[A-Z0-9&]$/;
  const SPECIAL_OK = /^[\p{L}\p{N}.,!?'"\-+#@:\/]$/u; // renderable, but needs production confirmation
  const PERSON_CM = 175;
  const glyphCache = new Map();
  let fontReady = null;

  function loadFont() {
    if (!fontReady) {
      fontReady = (document.fonts && document.fonts.load)
        ? Promise.all([document.fonts.load('200px "GV Block"', 'ABCMRSTWXYZ0123456789&'), document.fonts.load('200px "GV Block"', 'ÇĞİÖŞÜ')]).catch(() => null)
        : Promise.resolve();
    }
    return fontReady;
  }

  /* ---------------- glyph analysis: advance width + bulb positions along the stroke centreline ---------------- */
  function analyseGlyph(ch) {
    if (glyphCache.has(ch)) return glyphCache.get(ch);
    const F = 160, pad = 20;
    const c = document.createElement('canvas');
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.font = F + 'px "GV Block", Georgia, serif';
    const m = ctx.measureText(ch);
    const capM = ctx.measureText('H');
    const cap = capM.actualBoundingBoxAscent || F * 0.72;
    const w = Math.ceil(Math.max(m.width, 10) + pad * 2), h = Math.ceil(F * 1.3 + pad);
    c.width = w; c.height = h;
    ctx.font = F + 'px "GV Block", Georgia, serif';
    ctx.fillStyle = '#000'; ctx.textBaseline = 'alphabetic';
    const base = Math.round(F + pad / 2);
    ctx.fillText(ch, pad, base);
    const data = ctx.getImageData(0, 0, w, h).data;
    // chamfer distance transform (3-4)
    const N = w * h, INF = 1e9, d = new Float32Array(N);
    for (let i = 0; i < N; i++) d[i] = data[i * 4 + 3] > 127 ? INF : 0;
    for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
      const i = y * w + x; if (!d[i]) continue;
      d[i] = Math.min(d[i], d[i - 1] + 3, d[i - w] + 3, d[i - w - 1] + 4, d[i - w + 1] + 4);
    }
    for (let y = h - 2; y > 0; y--) for (let x = w - 2; x > 0; x--) {
      const i = y * w + x; if (!d[i]) continue;
      d[i] = Math.min(d[i], d[i + 1] + 3, d[i + w] + 3, d[i + w + 1] + 4, d[i + w - 1] + 4);
    }
    let maxD = 0;
    for (let i = 0; i < N; i++) if (d[i] < INF && d[i] > maxD) maxD = d[i];
    // typical half-stroke: 85th percentile of ridge values
    const ridge = [];
    for (let y = 1; y < h - 1; y++) for (let x = 1; x < w - 1; x++) {
      const i = y * w + x, v = d[i];
      if (v < 9) continue;
      if (v >= d[i - 1] && v >= d[i + 1] && v >= d[i - w] && v >= d[i + w] && v >= d[i - w - 1] && v >= d[i + w + 1] && v >= d[i - w + 1] && v >= d[i + w - 1]) ridge.push([x, y, v]);
    }
    const vals = ridge.map((r) => r[2]).sort((a, b) => a - b);
    const half = (vals[Math.floor(vals.length * 0.8)] || maxD) / 3; // px
    const minR = half * 0.62 * 3;
    const cands = ridge.filter((r) => r[2] >= minR).sort((a, b) => b[2] - a[2] || a[1] - b[1] || a[0] - b[0]);
    const spacing = half * 2.55, sp2 = spacing * spacing, pts = [];
    for (const [x, y] of cands) {
      let ok = true;
      for (const p of pts) { const dx = p[0] - x, dy = p[1] - y; if (dx * dx + dy * dy < sp2) { ok = false; break; } }
      if (ok) pts.push([x, y]);
    }
    const res = {
      adv: m.width / F,                        // em
      cap: cap / F,                            // em
      half: half / F,                          // em (half stroke width)
      bulbs: pts.map(([x, y]) => [(x - pad) / F, (y - base) / F]) // em, relative to baseline origin
    };
    glyphCache.set(ch, res);
    return res;
  }

  /* ---------------- colour helpers ---------------- */
  function shade(hex, f) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || ''); if (!m) return hex;
    const n = parseInt(m[1], 16); let r = n >> 16, g = (n >> 8) & 255, b = n & 255;
    const t = f < 0 ? 0 : 255, p = Math.abs(f);
    r = Math.round((t - r) * p + r); g = Math.round((t - g) * p + g); b = Math.round((t - b) * p + b);
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }
  const el = (tag, attrs, parent) => {
    const n = document.createElementNS(SVGNS, tag);
    for (const k in attrs) n.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(n);
    return n;
  };
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* ---------------- builder ---------------- */
  class Builder {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('[data-mb-config]').textContent);
      this.cfg.presets = (this.cfg.presets || []).map(Number).filter((n) => n > 0);
      this.t = this.cfg.strings;
      this.uid = root.dataset.sectionId;
      this.state = {
        mode: 'word', text: this.cfg.defaultText || '', sets: 1, picks: {},
        lighting: 'led', finish: 'smooth', color: (this.cfg.colors[0] || {}).id, customColor: '',
        height: this.cfg.defaultHeight, stack: /\s/.test((this.cfg.defaultText || '').trim()), view: 'studio'
      };
      this.started = false;
      this.q = (s) => root.querySelector(s);
      this.qa = (s) => root.querySelectorAll(s);
      this.restore();
      this.bind();
      loadFont().then(() => { this.syncControls(); this.render(); });
    }

    /* ----- state persistence (URL params for "Customize this set"; sessionStorage to survive quote form reload) ----- */
    restore() {
      const key = 'gv-mb-' + this.uid;
      try { const saved = JSON.parse(sessionStorage.getItem(key) || 'null'); if (saved) Object.assign(this.state, saved); } catch (e) {}
      const u = new URLSearchParams(location.search);
      if (u.get('text') !== null) { this.state.text = u.get('text'); this.state.mode = 'word'; }
      if (u.get('lighting') === 'led' || u.get('lighting') === 'bulbs') this.state.lighting = u.get('lighting');
      if (u.get('finish') === 'smooth' || u.get('finish') === 'plastered') this.state.finish = u.get('finish');
      if (u.get('height') && !isNaN(+u.get('height'))) this.state.height = this.clampH(+u.get('height'));
      if (u.get('sets') && +u.get('sets') > 0) this.state.sets = Math.min(99, Math.floor(+u.get('sets')));
      if (u.get('color') && this.cfg.colors.some((c) => c.id === u.get('color'))) this.state.color = u.get('color');
      if (!this.cfg.colors.some((c) => c.id === this.state.color)) this.state.color = (this.cfg.colors[0] || {}).id;
    }
    persist() { try { sessionStorage.setItem('gv-mb-' + this.uid, JSON.stringify(this.state)); } catch (e) {} }
    clampH(v) { return Math.min(this.cfg.maxHeight, Math.max(this.cfg.minHeight, Math.round(v))); }

    /* ----- derived data ----- */
    upper(s) { return (this.cfg.locale === 'tr' ? s.toLocaleUpperCase('tr-TR') : s.toLocaleUpperCase('en-US')); }
    analyseText() {
      const raw = this.upper(this.state.text || '').replace(/\s+/g, ' ').replace(/^ /, '');
      const chars = [...raw];
      const special = new Set(), rejected = new Set();
      const clean = [];
      for (const ch of chars) {
        if (ch === ' ') { clean.push(ch); continue; }
        if (STANDARD.test(ch)) clean.push(ch);
        else if (SPECIAL_OK.test(ch)) { clean.push(ch); special.add(ch); }
        else rejected.add(ch);
      }
      const text = clean.join('').trim();
      const physical = [...text].filter((c) => c !== ' ').length;
      const counts = {};
      for (const c of text) if (c !== ' ') counts[c] = (counts[c] || 0) + 1;
      return { text, chars: [...text].length, physical, counts, special: [...special], rejected: [...rejected] };
    }
    units() {
      if (this.state.mode === 'letters') {
        const picks = Object.entries(this.state.picks).filter(([, n]) => n > 0).map(([k, n]) => [k.replace(/^#/, ''), n]);
        const total = picks.reduce((s, [, n]) => s + n, 0);
        return { kind: 'letters', picks, perSet: total, total, sets: 1, label: picks.map(([c, n]) => c + ' × ' + n).join(', ') };
      }
      const a = this.analyseText();
      return { kind: 'word', a, perSet: a.physical, total: a.physical * this.state.sets, sets: this.state.sets, label: a.text };
    }
    colorObj() { return this.cfg.colors.find((c) => c.id === this.state.color) || this.cfg.colors[0] || { id: 'white', name: 'White', hex: '#f5f3ee' }; }
    isStandardHeight() { return this.cfg.presets.includes(this.state.height); }

    /* ----- events ----- */
    bind() {
      const r = this.root;
      r.addEventListener('input', (e) => this.onInput(e));
      r.addEventListener('change', (e) => this.onInput(e));
      r.addEventListener('click', (e) => this.onClick(e));
      let raf = 0;
      this.schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.render()); };
      window.addEventListener('resize', () => this.schedule(), { passive: true });
      const form = this.q('[data-mb-quote-form]');
      if (form) form.addEventListener('submit', (e) => this.onQuoteSubmit(e, form));
      // mobile sticky action bar appears while the builder is in view
      const bar = this.q('[data-mb-mobilebar]');
      if (bar && 'IntersectionObserver' in window) {
        new IntersectionObserver((ents) => ents.forEach((en) => bar.classList.toggle('is-visible', en.isIntersecting)), { threshold: 0.05 }).observe(this.q('.mb__controls'));
      }
    }
    started_() { if (!this.started) { this.started = true; window.GV && GV.track('marquee_start', { section: this.uid }); } }
    onInput(e) {
      const t = e.target, s = this.state;
      if (t.matches('[data-mb-text]')) { s.text = t.value.slice(0, this.cfg.maxChars); if (t.value.length > this.cfg.maxChars) t.value = s.text; }
      else if (t.matches('[data-mb-sets]')) s.sets = Math.max(1, Math.min(99, parseInt(t.value, 10) || 1));
      else if (t.matches('[name^="mb-lighting"]')) s.lighting = t.value;
      else if (t.matches('[name^="mb-finish"]')) s.finish = t.value;
      else if (t.matches('[name^="mb-color"]')) s.color = t.value;
      else if (t.matches('[data-mb-custom-color]')) s.customColor = t.value.slice(0, 80);
      else if (t.matches('[data-mb-range]')) s.height = this.clampH(+t.value);
      else if (t.matches('[data-mb-height-input]')) { if (e.type === 'change' || t.value.length >= 2) s.height = this.clampH(+t.value || this.cfg.defaultHeight); }
      else if (t.matches('[data-mb-stack]')) s.stack = t.checked;
      else if (t.matches('[data-mb-pick-qty]')) { const n = Math.max(0, Math.min(99, parseInt(t.value, 10) || 0)); const k = '#' + t.dataset.ch; s.picks[k] = n; if (!n) delete s.picks[k]; }
      else return;
      this.started_();
      this.changed(t);
    }
    onClick(e) {
      const b = e.target.closest('button'); if (!b || !this.root.contains(b)) return;
      const s = this.state, d = b.dataset;
      if (d.mbTab) { s.mode = d.mbTab; }
      else if (d.mbView) { s.view = d.mbView; }
      else if (d.mbPreset) { s.height = this.clampH(+d.mbPreset); }
      else if (d.mbSetsStep) { s.sets = Math.max(1, Math.min(99, s.sets + +d.mbSetsStep)); }
      else if (d.mbPick) { const k = '#' + d.mbPick; s.picks[k] = (s.picks[k] || 0) + 1; }
      else if (d.mbPickStep) { const c = '#' + d.ch; s.picks[c] = Math.max(0, (s.picks[c] || 0) + +d.mbPickStep); if (!s.picks[c]) delete s.picks[c]; }
      else if (d.mbPickRemove) { delete s.picks['#' + d.mbPickRemove]; }
      else if (d.mbUseSet !== undefined) {
        const p = this.cfg.sets[+d.mbUseSet]; if (!p) return;
        s.mode = 'word'; s.text = p.text; if (p.lighting) s.lighting = p.lighting; s.stack = !!p.stack;
      }
      else if (d.mbAddCart !== undefined) { e.preventDefault(); this.addToCart(b); return; }
      else return;
      e.preventDefault();
      this.started_();
      this.changed(b);
    }
    changed(src) {
      this.persist();
      this.syncControls(src);
      this.schedule();
      clearTimeout(this._tt);
      this._tt = setTimeout(() => window.GV && GV.track('marquee_change', this.payload()), 600);
    }

    /* ----- controls reflect state ----- */
    syncControls(src) {
      const s = this.state, u = this.units(), t = this.t;
      this.qa('[data-mb-tab]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.mbTab === s.mode)));
      this.qa('[data-mb-panel]').forEach((p) => (p.hidden = p.dataset.mbPanel !== s.mode));
      this.qa('[data-mb-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mbView === s.view)));
      const ti = this.q('[data-mb-text]'); if (ti && src !== ti) ti.value = s.text;
      const si = this.q('[data-mb-sets]'); if (si && src !== si) si.value = s.sets;
      const st = this.q('[data-mb-stack]'); if (st) { st.checked = s.stack; st.closest('label').hidden = !(u.kind === 'word' && u.a.text.includes(' ')); }
      this.qa('[name^="mb-lighting"]').forEach((i) => (i.checked = i.value === s.lighting));
      this.qa('[name^="mb-finish"]').forEach((i) => (i.checked = i.value === s.finish));
      this.qa('[name^="mb-color"]').forEach((i) => (i.checked = i.value === s.color));
      const cc = this.q('[data-mb-custom-wrap]'); if (cc) cc.hidden = !this.colorObj().custom;
      const cci = this.q('[data-mb-custom-color]'); if (cci && src !== cci) cci.value = s.customColor;
      const rg = this.q('[data-mb-range]');
      if (rg) { if (src !== rg) rg.value = s.height; rg.style.setProperty('--p', ((s.height - this.cfg.minHeight) / (this.cfg.maxHeight - this.cfg.minHeight) * 100) + '%'); rg.setAttribute('aria-valuetext', s.height + ' cm'); }
      const hi = this.q('[data-mb-height-input]'); if (hi && src !== hi) hi.value = s.height;
      this.qa('[data-mb-preset]').forEach((b) => b.setAttribute('aria-pressed', String(+b.dataset.mbPreset === s.height)));
      this.qa('[data-mb-sets-step]').forEach(() => {});
      // height label
      const hv = this.q('[data-mb-height-value]');
      if (hv) hv.innerHTML = s.height + ' cm' + (this.cfg.showInches ? ' <span class="small">/ ' + (s.height / 2.54).toFixed(1) + ' in</span>' : '');
      const hb = this.q('[data-mb-height-badge]');
      if (hb) { const std = this.isStandardHeight(); hb.textContent = std ? t.standard_size : t.custom_size; hb.className = 'mb__pill' + (std ? '' : ' mb__pill--custom'); }
      // counts + warnings
      if (u.kind === 'word') {
        const a = u.a;
        this.setText('[data-mb-chars]', a.chars);
        this.setText('[data-mb-per-set]', a.physical);
        this.setText('[data-mb-total]', u.total);
        const w = this.q('[data-mb-warn]');
        if (w) {
          const msgs = [];
          if (a.special.length) msgs.push(t.special_chars.replace('[chars]', a.special.join(' ')));
          if (a.rejected.length) msgs.push('<span class="mb__warn--err">' + t.rejected_chars.replace('[chars]', esc(a.rejected.join(' '))) + '</span>');
          w.innerHTML = msgs.join('<br>'); w.hidden = !msgs.length;
        }
      }
      // picked letters list
      const list = this.q('[data-mb-picked]');
      if (list && (src == null || !src.matches || !src.matches('[data-mb-pick-qty]'))) {
        const rows = Object.entries(s.picks).filter(([, n]) => n > 0).map(([k, n]) => [k.replace(/^#/, ''), n]);
        list.innerHTML = rows.length ? rows.map(([c, n]) =>
          '<div class="mb__picked-row"><span class="ch">' + esc(c) + '</span><span class="small muted">' + esc(t.quantity) + '</span>' +
          '<div class="qty" style="height:44px;grid-template-columns:40px 48px 40px"><button type="button" data-mb-pick-step="-1" data-ch="' + esc(c) + '" aria-label="−">−</button>' +
          '<input type="number" min="0" max="99" value="' + n + '" data-mb-pick-qty data-ch="' + esc(c) + '" aria-label="' + esc(t.quantity + ' ' + c) + '">' +
          '<button type="button" data-mb-pick-step="1" data-ch="' + esc(c) + '" aria-label="+">+</button></div>' +
          '<button type="button" class="rm" data-mb-pick-remove="' + esc(c) + '" aria-label="' + esc(t.remove + ' ' + c) + '">✕</button></div>').join('')
          : '<p class="small muted" style="margin:0">' + esc(t.pick_hint) + '</p>';
        this.setText('[data-mb-picked-total]', u.kind === 'letters' ? u.total : 0);
      }
      this.qa('[data-mb-use-set]').forEach((b) => { const p = this.cfg.sets[+b.dataset.mbUseSet]; b.setAttribute('aria-pressed', String(!!p && s.mode === 'word' && this.upper(p.text) === this.upper(s.text))); });
      this.updateSummary();
    }
    setText(sel, v) { this.qa(sel).forEach((n) => (n.textContent = v)); }

    payload() {
      const s = this.state, u = this.units(), c = this.colorObj();
      return {
        mode: s.mode, text: u.kind === 'word' ? u.a.text : '', letters: u.kind === 'letters' ? u.label : '',
        characters: u.kind === 'word' ? u.a.chars : u.perSet, letters_per_set: u.perSet, sets: u.sets, physical_total: u.total,
        lighting: s.lighting, finish: s.finish, color: c.name + (c.custom && s.customColor ? ' — ' + s.customColor : ''),
        height_cm: s.height, standard_height: this.isStandardHeight(), special: u.kind === 'word' ? u.a.special.join(' ') : ''
      };
    }

    /* ----- summary, quote fields, purchase availability ----- */
    updateSummary() {
      const t = this.t, p = this.payload(), s = this.state;
      const L = { led: t.led, bulbs: t.bulbs }, Fn = { smooth: t.smooth, plastered: t.plastered };
      const rows = [];
      if (p.mode === 'letters') rows.push([t.sum_letters, p.letters || '—']);
      else { rows.push([t.sum_text, p.text || '—']); rows.push([t.sum_characters, p.characters]); rows.push([t.sum_per_set, p.letters_per_set]); rows.push([t.sum_sets, p.sets]); }
      rows.push([t.sum_total, p.physical_total]);
      rows.push([t.sum_height, p.height_cm + ' cm' + (this.cfg.showInches ? ' (' + (p.height_cm / 2.54).toFixed(1) + ' in)' : '') + (p.standard_height ? '' : ' — ' + t.custom_size)]);
      rows.push([t.sum_lighting, L[p.lighting]]);
      rows.push([t.sum_finish, Fn[p.finish]]);
      rows.push([t.sum_color, p.color + (this.colorObj().custom ? ' — ' + t.color_confirm : '')]);
      if (p.special) rows.push([t.sum_custom, t.special_short.replace('[chars]', p.special)]);
      const dl = this.q('[data-mb-summary]');
      if (dl) dl.innerHTML = rows.map(([k, v]) => '<dt>' + esc(k) + '</dt><dd>' + esc(v) + '</dd>').join('');
      this.setText('[data-mb-bar-total]', p.physical_total);
      // hidden quote fields
      const map = {
        'Configuration mode': p.mode === 'letters' ? 'Individual letters & numbers' : 'Word / phrase',
        'Text': p.text, 'Individual letters': p.letters, 'Character count': p.mode === 'word' ? p.characters : '',
        'Letters per set': p.letters_per_set, 'Complete sets': p.sets, 'Total physical letters': p.physical_total,
        'Letter height': p.height_cm + ' cm' + (p.standard_height ? ' (standard preset)' : ' (custom height request)'),
        'Lighting': p.lighting === 'led' ? 'LED Outline' : 'Marquee Bulbs',
        'Surface finish': p.finish === 'smooth' ? 'Smooth XPS' : 'Plastered / textured finish on XPS',
        'Colour': p.color, 'Characters needing production confirmation': p.special
      };
      this.qa('[data-mb-field]').forEach((i) => { i.value = map[i.dataset.mbField] != null ? map[i.dataset.mbField] : ''; });
      const ready = p.physical_total > 0;
      this.qa('[data-mb-requires-units]').forEach((b) => (b.disabled = !ready));
      this.updatePurchase(p);
    }

    findVariant(p) {
      const prod = this.cfg.product; if (!prod || !prod.variants) return null;
      const names = (prod.options || []).map((o) => String(o.name || o).toLowerCase());
      const idx = (n) => names.indexOf(String(n).toLowerCase());
      const ih = idx(this.cfg.optHeight), il = idx(this.cfg.optLighting), iF = idx(this.cfg.optFinish);
      if (ih < 0 || il < 0 || iF < 0) return null;
      const wantL = (p.lighting === 'led' ? this.cfg.valLed : this.cfg.valBulbs).toLowerCase();
      const wantF = (p.finish === 'smooth' ? this.cfg.valSmooth : this.cfg.valPlastered).toLowerCase();
      return prod.variants.find((v) => {
        const o = v.options || [v.option1, v.option2, v.option3];
        return parseInt(o[ih], 10) === p.height_cm && String(o[il]).toLowerCase() === wantL && String(o[iF]).toLowerCase() === wantF;
      }) || null;
    }
    updatePurchase(p) {
      const box = this.q('[data-mb-purchase]'); if (!box) return;
      const t = this.t, reasonEl = this.q('[data-mb-purchase-reason]'), priceEl = this.q('[data-mb-price]'), btn = this.q('[data-mb-add-cart]');
      let reason = '';
      const v = this.findVariant(p);
      if (!p.physical_total) reason = t.reason_empty;
      else if (!p.standard_height) reason = t.reason_custom_height;
      else if (this.colorObj().custom) reason = t.reason_custom_color;
      else if (p.special) reason = t.reason_special;
      else if (!v) reason = t.reason_no_variant;
      else if (!v.available || !(v.price > 0)) reason = t.reason_unavailable;
      this.variant = reason ? null : v;
      if (btn) btn.disabled = !!reason;
      if (priceEl) priceEl.textContent = reason ? '—' : (window.GV ? GV.money(v.price * p.physical_total) : '');
      const each = this.q('[data-mb-price-each]');
      if (each) each.textContent = reason ? '' : t.price_each.replace('[price]', GV.money(v.price)).replace('[count]', p.physical_total);
      if (reasonEl) { reasonEl.textContent = reason; reasonEl.hidden = !reason; }
    }
    addToCart(btn) {
      const p = this.payload(), v = this.variant; if (!v) return;
      const cfgId = 'GV-' + Date.now().toString(36).toUpperCase();
      const t = this.t;
      const props = {};
      if (p.mode === 'letters') props[t.prop_letters] = p.letters; else { props[t.prop_text] = p.text; props[t.prop_sets] = String(p.sets); props[t.prop_per_set] = String(p.letters_per_set); }
      props[t.prop_color] = p.color;
      props['_configuration_id'] = cfgId;
      btn.disabled = true;
      GV.addToCart([{ id: v.id, quantity: p.physical_total, properties: props }])
        .catch((err) => { const r = this.q('[data-mb-purchase-reason]'); if (r) { r.hidden = false; r.textContent = err.message; } })
        .finally(() => (btn.disabled = false));
    }
    onQuoteSubmit(e, form) {
      const p = this.payload();
      if (!p.physical_total) { e.preventDefault(); const m = this.q('[data-mb-quote-msg]'); if (m) { m.hidden = false; m.textContent = this.t.reason_empty; } return; }
      this.updateSummary();
      const body = form.querySelector('[data-mb-body]');
      if (body) {
        const lines = [...this.qa('[data-mb-field]')].filter((i) => i.value).map((i) => i.dataset.mbField + ': ' + i.value);
        const notes = form.querySelector('[data-mb-notes]');
        body.value = 'MARQUEE LETTER QUOTE REQUEST\n' + lines.join('\n') + (notes && notes.value ? '\n\nNotes:\n' + notes.value : '');
      }
      this.persist();
      try { sessionStorage.setItem('gv-mb-submitted', '1'); } catch (er) {}
      window.GV && GV.track('quote_submit', p);
    }

    /* ---------------- preview rendering ---------------- */
    render() {
      const svg = this.q('[data-mb-svg]'); if (!svg) return;
      const stage = svg.parentElement;
      stage.dataset.view = this.state.view;
      const W = Math.max(320, stage.clientWidth), H = Math.max(200, stage.clientHeight);
      svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
      while (svg.firstChild) svg.removeChild(svg.firstChild);
      const s = this.state, ev = s.view === 'event', c = this.colorObj();
      const u = this.units();
      // characters to draw
      let rows;
      if (u.kind === 'letters') rows = [u.picks.flatMap(([ch, n]) => Array(Math.min(n, 6)).fill(ch))];
      else {
        const txt = u.a.text;
        rows = s.stack && txt.includes(' ') ? this.splitRows(txt) : [[...txt]];
      }
      rows = rows.filter((r) => r.length);
      const defs = el('defs', {}, svg);
      // backgrounds
      const bg = el('linearGradient', { id: 'bg' + this.uid, x1: 0, y1: 0, x2: 0, y2: 1 }, defs);
      el('stop', { offset: 0, 'stop-color': ev ? '#1d1915' : '#ece5d9' }, bg);
      el('stop', { offset: 1, 'stop-color': ev ? '#0f0d0b' : '#ddd4c6' }, bg);
      const fl = el('linearGradient', { id: 'fl' + this.uid, x1: 0, y1: 0, x2: 0, y2: 1 }, defs);
      el('stop', { offset: 0, 'stop-color': ev ? '#2a241e' : '#cbbfad' }, fl);
      el('stop', { offset: 1, 'stop-color': ev ? '#110f0d' : '#bdb09c' }, fl);
      let groundY = H * 0.8;
      el('rect', { x: 0, y: 0, width: W, height: H, fill: 'url(#bg' + this.uid + ')' }, svg);
      const floor = el('rect', { x: 0, y: groundY, width: W, height: H - groundY, fill: 'url(#fl' + this.uid + ')' }, svg);
      if (ev) {
        const rg = el('radialGradient', { id: 'spot' + this.uid, cx: .5, cy: .55, r: .6 }, defs);
        el('stop', { offset: 0, 'stop-color': '#5a3f22', 'stop-opacity': .55 }, rg);
        el('stop', { offset: 1, 'stop-color': '#000', 'stop-opacity': 0 }, rg);
        el('rect', { x: 0, y: 0, width: W, height: H, fill: 'url(#spot' + this.uid + ')' }, svg);
      }
      // filters
      const blur = el('filter', { id: 'glow' + this.uid, x: '-50%', y: '-50%', width: '200%', height: '200%' }, defs);
      el('feGaussianBlur', { stdDeviation: ev ? 5 : 2.5 }, blur);
      const pl = el('filter', { id: 'plaster' + this.uid, x: 0, y: 0, width: '100%', height: '100%' }, defs);
      el('feTurbulence', { type: 'fractalNoise', baseFrequency: '0.55', numOctaves: 3, seed: 7, result: 'n' }, pl);
      const dl = el('feDiffuseLighting', { in: 'n', 'lighting-color': '#ffffff', surfaceScale: 2.4, result: 'l' }, pl);
      el('feDistantLight', { azimuth: 225, elevation: 48 }, dl);
      el('feComposite', { in: 'l', in2: 'SourceGraphic', operator: 'arithmetic', k1: 1.15, k2: 0, k3: 0, k4: 0, result: 'm' }, pl);
      el('feComposite', { in: 'm', in2: 'SourceAlpha', operator: 'in' }, pl);
      // face paint
      let face = c.hex || '#f5f3ee';
      if (c.stripe) {
        const pat = el('pattern', { id: 'stripe' + this.uid, patternUnits: 'userSpaceOnUse', width: 26, height: 26, patternTransform: 'rotate(40)' }, defs);
        el('rect', { width: 26, height: 26, fill: '#f7f4ee' }, pat);
        el('rect', { width: 13, height: 26, fill: '#b3261e' }, pat);
        face = 'url(#stripe' + this.uid + ')';
      }
      const baseHex = c.stripe ? '#e9e4dc' : (c.hex || '#f5f3ee');
      const warm = (h, f) => { const a = shade(h, f); const m = /^#(..)(..)(..)$/.exec(a); return m ? '#' + [parseInt(m[1],16)+6, parseInt(m[2],16)+2, parseInt(m[3],16)-8].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('') : a; };
      const side = warm(baseHex, ev ? -0.55 : -0.2), side2 = warm(baseHex, ev ? -0.68 : -0.32);

      // empty state
      if (!rows.length) {
        const tx = el('text', { x: W / 2, y: H * 0.45, 'text-anchor': 'middle', fill: ev ? '#8d8478' : '#8d8478', 'font-family': 'var(--f-body)', 'font-size': 14 }, svg);
        tx.textContent = this.t.preview_empty;
        this.setStats(u, null);
        return;
      }

      // layout in "em" (1 em = font size); cap height defines letter height
      const g0 = analyseGlyph('H');
      const capEm = g0.cap || 0.72;
      const track = 0.06; // em gap between physical letters
      const rowW = rows.map((r) => r.reduce((sum, ch) => sum + (ch === ' ' ? 0.42 : analyseGlyph(ch).adv + track), 0) - track);
      const maxRowEm = Math.max(...rowW);
      const lineGapEm = capEm * 0.18;
      const totalHEm = rows.length * capEm + (rows.length - 1) * lineGapEm;
      // scale: person (175cm) and letter height (cm). em -> cm: 1em = height/capEm cm
      const cmPerEm = s.height / capEm;
      const wordWcm = maxRowEm * cmPerEm, wordHcm = totalHEm * cmPerEm;
      const personW = 52; // cm footprint of figure drawing
      const gapCm = Math.max(45, s.height * 0.4);
      const sceneWcm = personW + gapCm + wordWcm, sceneHcm = Math.max(PERSON_CM, wordHcm) * 1.18;
      const padX = W * 0.06;
      const pxPerCm = Math.min((W - padX * 2) / sceneWcm, (H * 0.74) / sceneHcm);
      const x0 = (W - sceneWcm * pxPerCm) / 2;
      const contentH = Math.max(PERSON_CM, wordHcm) * pxPerCm;
      const newGround = Math.min(H * 0.86, (H + contentH) / 2 + H * 0.04);
      if (Math.abs(newGround - groundY) > 1) { floor.setAttribute('y', newGround); floor.setAttribute('height', H - newGround); groundY = newGround; }
      // person silhouette
      this.drawPerson(svg, x0 + personW * pxPerCm * 0.5, groundY, PERSON_CM * pxPerCm, ev);
      // letters
      const fs = cmPerEm * pxPerCm; // px per em
      const depth = Math.max(3, fs * 0.075);
      const lettersX = x0 + (personW + gapCm) * pxPerCm;
      const gAll = el('g', {}, svg);
      const glowLayer = el('g', { filter: 'url(#glow' + this.uid + ')', opacity: ev ? 0.95 : 0.55 }, null);
      rows.forEach((row, ri) => {
        const rw = rowW[ri] * fs;
        let x = lettersX + (maxRowEm * fs - rw) / 2;
        const baseY = groundY - (rows.length - 1 - ri) * (capEm + lineGapEm) * fs;
        for (const ch of row) {
          if (ch === ' ') { x += 0.42 * fs; continue; }
          const gi = analyseGlyph(ch);
          this.drawLetter(gAll, glowLayer, ch, gi, x, baseY, fs, depth, face, side, side2, ev);
          x += (gi.adv + track) * fs;
        }
      });
      gAll.appendChild(glowLayer);
      // height dimension line
      const firstH = capEm * fs;
      const dimX = lettersX - Math.max(10, fs * 0.08);
      const dimCol = ev ? '#cdb07a' : '#7a6a4f';
      const top = groundY - (rows.length - 1) * (capEm + lineGapEm) * fs - firstH;
      const rowTop = groundY - firstH;
      el('line', { x1: dimX, y1: rowTop, x2: dimX, y2: groundY, stroke: dimCol, 'stroke-width': 1 }, svg);
      el('line', { x1: dimX - 4, y1: rowTop, x2: dimX + 4, y2: rowTop, stroke: dimCol }, svg);
      el('line', { x1: dimX - 4, y1: groundY, x2: dimX + 4, y2: groundY, stroke: dimCol }, svg);
      const lab = el('text', { x: dimX - 7, y: (rowTop + groundY) / 2, 'text-anchor': 'end', 'dominant-baseline': 'middle', fill: dimCol, 'font-size': 12, 'font-family': 'GV Sans, sans-serif', 'font-weight': 600 }, svg);
      lab.textContent = s.height + ' cm';
      void top;
      this.setStats(u, wordWcm);
    }
    splitRows(txt) {
      const words = txt.split(' ');
      if (words.length < 2) return [[...txt]];
      let best = null;
      for (let i = 1; i < words.length; i++) {
        const a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
        const score = Math.abs(a.length - b.length);
        if (!best || score < best.s) best = { s: score, r: [[...a], [...b]] };
      }
      return best.r;
    }
    setStats(u, wcm) {
      this.setText('[data-mb-stat-units]', u.total);
      this.setText('[data-mb-stat-height]', this.state.height + ' cm');
      this.setText('[data-mb-stat-sets]', u.kind === 'word' ? u.sets : '—');
      const w = this.q('[data-mb-stat-width]');
      if (w) w.textContent = wcm ? '≈ ' + (wcm >= 100 ? (Math.round(wcm / 10) / 10).toLocaleString(this.cfg.locale) + ' m' : Math.round(wcm) + ' cm') : '—';
    }
    drawPerson(svg, cx, groundY, hpx, ev) {
      const k = hpx / 175, col = ev ? '#3b342d' : '#b8ad9c';
      const g = el('g', { fill: col, opacity: ev ? 0.9 : 0.75, 'aria-hidden': 'true' }, svg);
      el('circle', { cx, cy: groundY - 163 * k, r: 11.5 * k }, g);
      el('path', { d: `M${cx - 20 * k} ${groundY - 146 * k} Q${cx} ${groundY - 152 * k} ${cx + 20 * k} ${groundY - 146 * k} L${cx + 24 * k} ${groundY - 92 * k} L${cx + 15 * k} ${groundY - 90 * k} L${cx + 12 * k} ${groundY} L${cx + 2 * k} ${groundY} L${cx} ${groundY - 80 * k} L${cx - 2 * k} ${groundY} L${cx - 12 * k} ${groundY} L${cx - 15 * k} ${groundY - 90 * k} L${cx - 24 * k} ${groundY - 92 * k} Z` }, g);
      const t = el('text', { x: cx, y: groundY + 16, 'text-anchor': 'middle', fill: ev ? '#8d8478' : '#8a7f70', 'font-size': 11, 'font-family': 'GV Sans, sans-serif' }, svg);
      t.textContent = '175 cm';
    }
    drawLetter(parent, glowLayer, ch, gi, x, baseY, fs, depth, face, side, side2, ev) {
      const s = this.state, uid = this.uid;
      const g = el('g', {}, parent);
      const common = { x, y: baseY, 'font-size': fs, 'font-family': '"GV Block", Georgia, serif' };
      // extrusion (side returns): stacked offsets up-right for depth
      const steps = Math.min(14, Math.max(4, Math.round(depth / 1.2)));
      for (let i = steps; i >= 1; i--) {
        const off = (depth / steps) * i;
        const tx = el('text', Object.assign({}, common, { x: x + off * 0.85, y: baseY - off * 0.55, fill: i === steps ? side2 : side }), g);
        tx.textContent = ch;
      }
      // face
      const id = 'c' + uid + '_' + Math.round(x) + '_' + Math.round(baseY);
      const faceT = el('text', Object.assign({}, common, { fill: face, filter: s.finish === 'plastered' ? 'url(#plaster' + uid + ')' : '' }), g);
      faceT.textContent = ch;
      if (s.finish === 'smooth') {
        const sheen = el('text', Object.assign({}, common, { fill: '#ffffff', opacity: ev ? 0.04 : 0.1 }), g); sheen.textContent = ch;
      }
      // edge definition
      const edge = el('text', Object.assign({}, common, { fill: 'none', stroke: ev ? 'rgba(0,0,0,.35)' : 'rgba(0,0,0,.18)', 'stroke-width': Math.max(.6, fs * 0.004) }), g); edge.textContent = ch;

      if (s.lighting === 'led') {
        // inner contour light line, inset from the outer edge, inside a recessed groove
        const cp = el('clipPath', { id }, g);
        const ct = el('text', common, cp); ct.textContent = ch;
        const inner = el('g', { 'clip-path': 'url(#' + id + ')' }, g);
        const half = gi.half * fs;
        const a = half * 0.36, w = Math.max(1.2, half * 0.2), grv = Math.max(1, half * 0.12);
        const mk = (sw, col, extra) => { const n = el('text', Object.assign({}, common, { fill: 'none', stroke: col, 'stroke-width': 2 * sw, 'stroke-linejoin': 'round' }, extra || {}), inner); n.textContent = ch; return n; };
        mk(a + w + grv, ev ? '#3b2f22' : 'rgba(80,62,40,.55)');
        mk(a + w, ev ? '#fff1d6' : '#ffe9c2');
        mk(a, typeof face === 'string' && face.startsWith('url') ? 'none' : face);
        if (typeof face === 'string' && face.startsWith('url')) { const r = el('text', Object.assign({}, common, { fill: 'none', stroke: face, 'stroke-width': 2 * a }), inner); r.textContent = ch; }
        // glow (spills slightly in event view)
        const gl = el('text', Object.assign({}, common, { fill: 'none', stroke: ev ? '#ffd79a' : '#ffdca6', 'stroke-width': 2 * (a + w), opacity: 0.9 }), null); gl.textContent = ch;
        const gclip = el('g', ev ? {} : { 'clip-path': 'url(#' + id + ')' }, glowLayer); gclip.appendChild(gl);
        if (ev) { const mask = el('text', Object.assign({}, common, { fill: 'none', stroke: '#000', 'stroke-width': 2 * a, opacity: .0 }), gclip); mask.textContent = ch; }
      } else {
        const r = Math.max(1.6, gi.half * fs * 0.44);
        const bulbG = el('g', {}, g);
        for (const [bx, by] of gi.bulbs) {
          const cx = x + bx * fs, cy = baseY + by * fs;
          el('circle', { cx, cy, r: r * 1.32, fill: ev ? '#2a2119' : 'rgba(70,55,38,.4)' }, bulbG);
          el('circle', { 'data-bulb': '', cx, cy, r, fill: ev ? '#fff4dc' : '#fff6e4', stroke: ev ? '#ffd28a' : '#e9c58d', 'stroke-width': Math.max(.5, r * 0.18) }, bulbG);
          el('circle', { cx: cx - r * 0.3, cy: cy - r * 0.3, r: r * 0.35, fill: '#fff', opacity: .9 }, bulbG);
          el('circle', { cx, cy, r: r * (ev ? 2.2 : 1.6), fill: '#ffcf86', opacity: ev ? 0.5 : 0.22 }, glowLayer);
        }
      }
    }
  }

  function init(root) { if (!root.__gvmb) root.__gvmb = new Builder(root); }
  document.querySelectorAll('[data-marquee-builder]').forEach(init);
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-marquee-builder]').forEach(init));
})();
